import React, { useEffect, useMemo, useState } from 'react';
import { PageShell, Badge, DataTable } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { ROLES } from '../../permissions';
import { STAFF_ROLES } from '../../config/plans';
import {
  formatDoctorName, randomPassword, suggestStaffEmail,
} from '../../utils/staffLogin';
import AccessControlPanel from '../../components/admin/AccessControlPanel';
import { DENTAL_SPECIALTY_OPTIONS } from '../../utils/dentalSpecialty';

const LOGIN_URL = 'https://networks.hcen.in/login';
const CREATE_ROLES = STAFF_ROLES.filter((r) => r.id !== 'manager');

const emptyForm = {
  role: 'doctor',
  name: '',
  email: '',
  password: '',
  specialty: '',
  department: '',
  phone: '',
  enableDentalOp: false,
};

function QuotaPanel({ quotas, hospital }) {
  if (!quotas) return null;
  const { limits, usage } = quotas;
  return (
    <div className="quota-panel card mb">
      <div className="staff-admin-header">
        <div>
          <h3>Your login limits (set by HCEN)</h3>
          <p className="hint">
            HCEN decides how many doctor, reception, pharmacy… logins you get.
            You create each person here — no AWS console needed.
          </p>
        </div>
        {hospital?.plan && <Badge tone="info">{hospital.plan} plan</Badge>}
      </div>
      <p className="hint">Used {quotas.totalUsed} of {quotas.totalMax} total staff logins</p>
      <div className="quota-bars">
        {STAFF_ROLES.map((r) => {
          const limit = limits?.[r.id] ?? 0;
          const used = usage?.[r.id] ?? 0;
          const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
          const full = limit > 0 && used >= limit;
          return (
            <div key={r.id} className="quota-bar-row">
              <span className="quota-bar-label">{r.label}</span>
              <div className="quota-bar-track">
                <div className={`quota-bar-fill ${full ? 'full' : ''}`} style={{ width: `${pct}%` }} />
              </div>
              <span className={`quota-bar-count ${full ? 'full' : ''}`}>{used}/{limit}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CredentialCard({ creds, onClose }) {
  const text = `HCEN Networks login\nURL: ${creds.loginUrl}\nEmail: ${creds.email}\nPassword: ${creds.password}\nName: ${creds.name}\nRole: ${creds.roleLabel}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="credential-card card mb">
      <h3>Login created — share with staff</h3>
      <p className="hint">Staff sign in at <strong>{LOGIN_URL}</strong></p>
      <dl className="credential-dl">
        <dt>Name</dt><dd>{creds.name}</dd>
        <dt>Role</dt><dd>{creds.roleLabel}</dd>
        {creds.specialty && <><dt>Specialty</dt><dd>{creds.specialty}</dd></>}
        <dt>Email</dt><dd><code>{creds.email}</code></dd>
        <dt>Password</dt><dd><code>{creds.password}</code></dd>
      </dl>
      <div className="row-actions">
        <button type="button" className="btn btn-primary" onClick={copy}>Copy details</button>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

export default function HospitalAdmin() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getStaff(), []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [emailTouched, setEmailTouched] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '', role: '', password: '', active: true, specialty: '', department: '', phone: '',
  });
  const [roleFilter, setRoleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [resetCreds, setResetCreds] = useState(null);
  const [createdCreds, setCreatedCreds] = useState(null);

  const staff = data?.staff || [];
  const quotas = data?.quotas;
  const hospital = data?.hospital;
  const slug = hospital?.slug || 'hospital';

  const canAddRole = (roleId) => {
    if (!quotas?.limits) return true;
    const limit = quotas.limits[roleId] ?? 0;
    const used = quotas.usage?.[roleId] ?? 0;
    return limit === 0 || used < limit;
  };

  const roleIndex = useMemo(() => {
    const counts = {};
    staff.forEach((s) => {
      counts[s.role] = (counts[s.role] || 0) + 1;
    });
    return (role) => (counts[role] || 0) + 1;
  }, [staff]);

  const applySuggestedEmail = (role, name, force = false) => {
    if (emailTouched && !force) return;
    const email = suggestStaffEmail({ role, name, slug, index: roleIndex(role) });
    setForm((f) => ({ ...f, email }));
  };

  useEffect(() => {
    if (!showForm || emailTouched) return;
    applySuggestedEmail(form.role, form.name);
  }, [form.role, form.name, showForm, slug, emailTouched, roleIndex]);

  const pickRole = (roleId) => {
    if (!canAddRole(roleId)) return;
    setForm((f) => {
      const next = { ...f, role: roleId };
      if (roleId === 'doctor' && f.name && !/^dr\.?\s/i.test(f.name)) {
        next.name = formatDoctorName(f.name.replace(/^dr\.?\s*/i, ''));
      }
      return next;
    });
    setShowForm(true);
    setEmailTouched(false);
  };

  const onNameChange = (name) => {
    let displayName = name;
    if (form.role === 'doctor' && name && !/^dr\.?\s/i.test(name)) {
      displayName = formatDoctorName(name.replace(/^dr\.?\s*/i, ''));
    }
    setForm((f) => ({ ...f, name: displayName }));
  };

  const createStaff = async (e) => {
    e.preventDefault();
    setBusy('create');
    setMsg('');
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.toLowerCase().trim(),
        role: form.role,
        password: form.password,
      };
      if (form.role === 'doctor' && form.specialty) payload.specialty = form.specialty.trim();
      if (form.department) payload.department = form.department.trim();
      if (form.phone) payload.phone = form.phone.trim();
      if (form.role === 'doctor') payload.enableDentalOp = !!form.enableDentalOp;

      const res = await ipApi.createStaff(payload);
      setForm({ ...emptyForm, role: form.role });
      setShowForm(false);
      setEmailTouched(false);
      setCreatedCreds({
        name: payload.name,
        email: payload.email,
        password: form.password,
        roleLabel: ROLES[form.role]?.label || form.role,
        specialty: form.specialty,
        enableDentalOp: !!form.enableDentalOp,
        loginUrl: res.credentials?.loginUrl || LOGIN_URL,
      });
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const startEdit = (row) => {
    setEditId(row.staffId);
    setEditForm({
      name: row.name,
      role: row.role,
      password: '',
      active: row.active !== false,
      specialty: row.specialty || '',
      department: row.department || '',
      phone: row.phone || '',
      enableDentalOp: !!row.enableDentalOp,
    });
  };

  const saveEdit = async () => {
    setBusy(editId);
    setMsg('');
    try {
      const body = {
        name: editForm.name,
        role: editForm.role,
        active: editForm.active,
        specialty: editForm.specialty,
        department: editForm.department,
        phone: editForm.phone,
        enableDentalOp: !!editForm.enableDentalOp,
      };
      if (editForm.password) body.password = editForm.password;
      await ipApi.patchStaff(editId, body);
      setEditId(null);
      setMsg('Staff updated.');
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleActive = async (row) => {
    setBusy(row.staffId);
    try {
      await ipApi.patchStaff(row.staffId, { active: row.active === false });
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const resetPassword = async (row) => {
    if (!window.confirm(`Reset password for ${row.name}?`)) return;
    setBusy(`reset-${row.staffId}`);
    setMsg('');
    try {
      const res = await ipApi.resetStaffPassword(row.staffId);
      setResetCreds({ name: row.name, email: res.email, password: res.temporaryPassword });
      setMsg('Password reset — share with staff.');
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const filteredStaff = (roleFilter === 'all' ? staff : staff.filter((s) => s.role === roleFilter))
    .filter((s) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
    });

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (r) => (
        <div>
          <strong>{r.name}</strong>
          {r.specialty && <div className="hint">{r.specialty}</div>}
        </div>
      ),
    },
    { key: 'email', label: 'Login email' },
    { key: 'role', label: 'Role', render: (r) => ROLES[r.role]?.label || r.role },
    {
      key: 'active',
      label: 'Status',
      render: (r) => (
        <Badge tone={r.active !== false ? 'success' : 'warning'}>
          {r.active !== false ? 'Active' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.staffId} onClick={() => startEdit(r)}>Edit</button>
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy === `reset-${r.staffId}`} onClick={() => resetPassword(r)}>Reset pwd</button>
          {r.staffId !== user?.staffId && (
            <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.staffId} onClick={() => toggleActive(r)}>
              {r.active === false ? 'Enable' : 'Disable'}
            </button>
          )}
        </div>
      ),
    },
  ];

  const atCap = quotas && quotas.totalUsed >= quotas.totalMax;
  const selectedRoleMeta = STAFF_ROLES.find((r) => r.id === form.role);

  return (
    <PageShell
      title="Staff & logins"
      subtitle={`${hospital?.name || user?.hospital || 'Hospital'} — add users, roles, and login accounts`}
      actions={
        <button
          type="button"
          className="btn btn-primary"
          disabled={atCap}
          onClick={() => { setShowForm((s) => !s); setCreatedCreds(null); }}
        >
          {showForm ? 'Close form' : '+ Add staff login'}
        </button>
      }
    >
      <div className="workflow-banner card mb">
        <strong>How it works</strong>
        <ol className="workflow-steps">
          <li><strong>HCEN</strong> onboards your hospital and sets limits (e.g. 10 doctors, 4 reception).</li>
          <li><strong>You (hospital owner)</strong> create each doctor, receptionist, pharmacy… login here.</li>
          <li>Staff sign in at <a href={LOGIN_URL} target="_blank" rel="noreferrer">{LOGIN_URL}</a> — nothing in AWS console.</li>
        </ol>
        {slug && <p className="hint">Hospital email domain: <code>@{slug}.hcen</code></p>}
      </div>

      <QuotaPanel quotas={quotas} hospital={hospital} />
      <AccessControlPanel />

      {createdCreds && <CredentialCard creds={createdCreds} onClose={() => setCreatedCreds(null)} />}
      {resetCreds && (
        <div className="credential-card card mb">
          <h3>Password reset — {resetCreds.name}</h3>
          <dl className="credential-dl">
            <dt>Email</dt><dd><code>{resetCreds.email}</code></dd>
            <dt>New password</dt><dd><code>{resetCreds.password}</code></dd>
          </dl>
          <button type="button" className="btn btn-secondary" onClick={() => setResetCreds(null)}>Done</button>
        </div>
      )}

      {error && <div className="error mb">{error}</div>}
      {msg && <p className={`mb ${msg.includes('limit') ? 'error' : 'hint'}`}>{msg}</p>}

      {!showForm && (
        <div className="role-picker card mb">
          <h3>Quick add by role</h3>
          <p className="hint">Pick a role — only slots HCEN gave you are available.</p>
          <div className="role-picker-grid">
            {CREATE_ROLES.map((r) => {
              const full = !canAddRole(r.id);
              const used = quotas?.usage?.[r.id] ?? 0;
              const limit = quotas?.limits?.[r.id] ?? 0;
              return (
                <button
                  key={r.id}
                  type="button"
                  className={`role-picker-card ${full ? 'disabled' : ''}`}
                  disabled={full || atCap}
                  onClick={() => pickRole(r.id)}
                >
                  <span className="role-picker-title">{r.label}</span>
                  <span className="role-picker-desc">{r.description}</span>
                  <span className={`role-picker-count ${full ? 'full' : ''}`}>{used}/{limit}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showForm && (
        <form className="staff-create-form card mb" onSubmit={createStaff}>
          <h3>Add {selectedRoleMeta?.label || form.role}</h3>
          <p className="hint">{selectedRoleMeta?.description}</p>

          <div className="form-grid">
            <div className="field">
              <label>Role *</label>
              <select
                className="input"
                value={form.role}
                onChange={(e) => { setForm({ ...form, role: e.target.value }); setEmailTouched(false); }}
              >
                {CREATE_ROLES.map((r) => (
                  <option key={r.id} value={r.id} disabled={!canAddRole(r.id)}>
                    {r.label}{!canAddRole(r.id) ? ' (limit reached)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>{form.role === 'doctor' ? 'Doctor name *' : 'Full name *'}</label>
              <input
                className="input"
                required
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder={form.role === 'doctor' ? 'Raghu Nandan' : 'Staff name'}
              />
            </div>

            {form.role === 'doctor' && (
              <>
                <div className="field">
                  <label>Specialty</label>
                  <input
                    className="input"
                    list="hcen-specialty-options"
                    value={form.specialty}
                    onChange={(e) => setForm({ ...form, specialty: e.target.value })}
                    placeholder="General Medicine, Dental / Oral Health…"
                  />
                  <datalist id="hcen-specialty-options">
                    {['General Medicine', 'Pediatrics', 'Cardiology', ...DENTAL_SPECIALTY_OPTIONS].map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                <div className="field full">
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!form.enableDentalOp}
                      onChange={(e) => setForm({ ...form, enableDentalOp: e.target.checked })}
                    />
                    Enable Dental OP workspace (odontogram + dental procedures on EMR)
                  </label>
                  <div className="hint">Only this doctor login will see the dental chart. Pick a Dental specialty or tick this box.</div>
                </div>
              </>
            )}

            <div className="field">
              <label>Login email *</label>
              <div className="input-with-btn">
                <input
                  className="input"
                  type="email"
                  required
                  placeholder={`dr.name@${slug}.hcen`}
                  value={form.email}
                  onChange={(e) => { setEmailTouched(true); setForm({ ...form, email: e.target.value }); }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => { setEmailTouched(false); applySuggestedEmail(form.role, form.name, true); }}
                >
                  Suggest
                </button>
              </div>
            </div>

            <div className="field">
              <label>Password *</label>
              <div className="input-with-btn">
                <input
                  className="input"
                  type="text"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setForm({ ...form, password: randomPassword() })}
                >
                  Generate
                </button>
              </div>
            </div>

            <div className="field">
              <label>Phone (optional)</label>
              <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>

            {(form.role === 'doctor' || form.role === 'nurse') && (
              <div className="field">
                <label>Department (optional)</label>
                <input className="input" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="OPD, ICU…" />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy === 'create' || !canAddRole(form.role)}>
              Create login
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {editId && (
        <div className="form-grid card mb">
          <div className="field"><label>Name</label><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
          {editForm.role === 'doctor' && (
            <>
              <div className="field">
                <label>Specialty</label>
                <input className="input" list="hcen-specialty-options-edit" value={editForm.specialty} onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })} />
                <datalist id="hcen-specialty-options-edit">
                  {['General Medicine', 'Pediatrics', 'Cardiology', ...DENTAL_SPECIALTY_OPTIONS].map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </div>
              <div className="field full">
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={!!editForm.enableDentalOp} onChange={(e) => setEditForm({ ...editForm, enableDentalOp: e.target.checked })} />
                  Dental OP workspace
                </label>
              </div>
            </>
          )}
          <div className="field">
            <label>Role</label>
            <select className="input" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
              {Object.keys(ROLES).map((r) => <option key={r} value={r}>{ROLES[r]?.label || r}</option>)}
            </select>
          </div>
          <div className="field"><label>New password (optional)</label><input className="input" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} /></div>
          <div className="form-actions full row-actions">
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={saveEdit}>Save</button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditId(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card mb row-actions" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder="Search staff by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="hint">{filteredStaff.length} shown</span>
      </div>

      <div className="staff-filter-tabs mb">
        <button type="button" className={`tab-chip ${roleFilter === 'all' ? 'active' : ''}`} onClick={() => setRoleFilter('all')}>All ({staff.length})</button>
        {STAFF_ROLES.map((r) => {
          const count = staff.filter((s) => s.role === r.id).length;
          if (!count) return null;
          return (
            <button key={r.id} type="button" className={`tab-chip ${roleFilter === r.id ? 'active' : ''}`} onClick={() => setRoleFilter(r.id)}>
              {r.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? <p className="hint">Loading staff…</p> : <DataTable columns={columns} rows={filteredStaff} keyField="staffId" />}
    </PageShell>
  );
}
